<template>
  <div
    class="trezo-card bg-white dark:bg-[#0c1427] mb-[25px] p-[20px] md:p-[25px] rounded-md"
  >
    <div class="trezo-card-content relative">
      <span class="block"> {{ directT('dashboard.crm.revenueGrowth') }} </span>
      <h5 class="mb-0 mt-[3px] text-[20px]" v-if="!loading">{{ currencySymbol }}{{ totalRevenue.toLocaleString() }}</h5>
      <h5 class="mb-0 mt-[3px] text-[20px]" v-else>
        <span class="animate-pulse">...</span>
      </h5>
      <div
        class="absolute -top-[28px] ltr:-right-[9px] rtl:-left-[9px] max-w-[120px]"
      >
        <apexchart
          v-if="isClient && !loading"
          type="area"
          height="95"
          :options="revenueGrowth"
          :series="revenue"
        ></apexchart>
        <div v-else-if="isClient && loading" class="h-[95px] flex items-center justify-center">
          <span class="animate-pulse">...</span>
        </div>
      </div>
      <div class="mt-[25px] md:mt-[34px] flex items-center justify-between">
        <span
          class="inline-block text-sm py-[1px] px-[8.3px] border rounded-xl"
          :class="growthPercentage >= 0 ? 'text-success-700 border-success-300 bg-success-100' : 'text-danger-700 border-danger-300 bg-danger-100'"
        >
          {{ growthPercentage >= 0 ? '+' : '' }}{{ growthPercentage }}%
        </span>
        <span class="block text-sm"> {{ directT('dashboard.last7Days') }} </span>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, ref, onMounted, computed, watch } from "vue";
import { useI18n } from "vue-i18n";
import { supabase } from "@/lib/supabaseClient";

// Direct import of translation files for manual translation lookup
import enTranslations from '@/i18n/locales/en.json';
import heTranslations from '@/i18n/locales/he.json';
import arTranslations from '@/i18n/locales/ar.json';

// Create type for translation object structure
type TranslationObject = Record<string, any>;

interface Deal {
  id: string;
  amount: number | string;
  created_at: string;
  title: string;
  status: string;
}

interface Organization {
  id: string;
  currency: string;
  [key: string]: any; // Allow other properties
}

export default defineComponent({
  name: "RevenueGrowthChart",
  setup() {
    const { locale } = useI18n();
    const isClient = ref(false);
    const loading = ref(true);
    const deals = ref<Deal[]>([]);
    const totalRevenue = ref(0);
    const growthPercentage = ref(0);
    const dailyRevenue = ref<number[]>([0, 0, 0, 0, 0, 0, 0]); // Last 7 days
    const currencySymbol = ref('$'); // Default currency symbol
    
    // Store direct references to translation files
    const translationFiles = {
      en: enTranslations,
      he: heTranslations,
      ar: arTranslations
    };

    // Direct translation function that bypasses vue-i18n
    const directT = (key: string) => {
      const parts = key.split('.');
      let result: TranslationObject = translationFiles[locale.value as keyof typeof translationFiles];
      
      for (const part of parts) {
        if (!result || !(part in result)) {
          console.warn(`Missing translation key: ${key} in ${locale.value} locale`);
          // Fallback to English if translation not found
          let enResult: TranslationObject = translationFiles.en;
          for (const fallbackPart of parts) {
            if (!enResult || !(fallbackPart in enResult)) return key;
            enResult = enResult[fallbackPart as keyof typeof enResult] as TranslationObject;
          }
          return enResult;
        }
        result = result[part as keyof typeof result] as TranslationObject;
      }
      return result;
    };
    
    const revenue = computed(() => {
      return [
        {
          name: "Revenue Growth",
          data: dailyRevenue.value,
        },
      ];
    });

    // Computed property for revenueGrowth options to ensure currency symbol is reactive
    const revenueGrowth = computed(() => {
      return {
        chart: {
          type: "area",
          height: 95,
          zoom: {
            enabled: false,
          },
          toolbar: {
            show: false,
          },
        },
        colors: ["#3584FC"],
        dataLabels: {
          enabled: false,
        },
        grid: {
          show: false,
          borderColor: "#ECEEF2",
        },
        stroke: {
          curve: "straight",
          width: 1,
        },
        xaxis: {
          categories: ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"],
          axisTicks: {
            show: false,
            color: "#B1BBC8",
          },
          axisBorder: {
            show: false,
            color: "#B1BBC8",
          },
          labels: {
            show: false,
            style: {
              colors: "#8695AA",
              fontSize: "12px",
            },
          },
        },
        yaxis: {
          show: false,
          labels: {
            formatter: (val: string) => {
              return currencySymbol.value + val;
            },
            style: {
              colors: "#64748B",
              fontSize: "12px",
            },
          },
        },
        legend: {
          show: false,
          position: "top",
          fontSize: "12px",
          horizontalAlign: "left",
          itemMargin: {
            horizontal: 8,
            vertical: 0,
          },
          labels: {
            colors: "#64748B",
          },
          markers: {
            width: 9,
            height: 9,
            offsetX: -2,
            offsetY: -0.5,
            radius: 2,
          },
        },
        tooltip: {
          y: {
            formatter: (val: number) => {
              return currencySymbol.value + val.toLocaleString();
            }
          }
        }
      };
    });

    // Fetch deals data from Supabase
    const fetchDealsData = async () => {
      try {
        loading.value = true;
        
        // Get current user's organization
        const { data: { user } } = await supabase.auth.getUser();
        
        if (!user) {
          console.warn('User not authenticated');
          loading.value = false;
          return;
        }
        
        // Get the user's organization ID
        const { data: userProfile, error: profileError } = await supabase
          .from('user_profiles')
          .select('org_id')
          .eq('user_id', user.id)
          .single();
        
        if (profileError || !userProfile?.org_id) {
          console.warn('User has no organization');
          loading.value = false;
          return;
        }
        
        const organizationId = userProfile.org_id as string;
        
        // Get organization's currency
        const { data: orgData, error: orgError } = await supabase
          .from('organizations')
          .select('currency')
          .eq('id', organizationId)
          .single();
          
        if (orgError) {
          console.error('Error fetching organization data:', orgError);
        } else if (orgData && orgData.currency) {
          currencySymbol.value = (orgData as Organization).currency;
        }
        
        // Calculate date ranges for the last 14 days (7 days for comparison + 7 days for display)
        const today = new Date();
        const twoWeeksAgo = new Date(today);
        twoWeeksAgo.setDate(today.getDate() - 14);
        
        // Fetch deals from the last 14 days
        const { data, error } = await supabase
          .from('deals')
          .select('id, amount, created_at, title, status')
          .eq('organization_id', organizationId)
          .gte('created_at', twoWeeksAgo.toISOString())
          .order('created_at', { ascending: false });
        
        if (error) {
          console.error('Error fetching deals:', error);
          loading.value = false;
          return;
        }
        
        // Process the deals data
        deals.value = data as Deal[];
        
        // Calculate revenue for the last 7 days
        const last7Days: Date[] = [];
        for (let i = 6; i >= 0; i--) {
          const date = new Date(today);
          date.setDate(today.getDate() - i);
          date.setHours(0, 0, 0, 0);
          last7Days.push(date);
        }
        
        // Calculate revenue for each of the last 7 days
        const revenueByDay: number[] = Array(7).fill(0);
        let currentPeriodTotal = 0;
        
        deals.value.forEach(deal => {
          const dealAmount = typeof deal.amount === 'string' ? parseFloat(deal.amount) : deal.amount;
          if (isNaN(dealAmount)) return;
          
          const dealDate = new Date(deal.created_at);
          dealDate.setHours(0, 0, 0, 0);
          
          // Check if the deal is from the last 7 days
          for (let i = 0; i < 7; i++) {
            if (dealDate.getTime() === last7Days[i].getTime()) {
              revenueByDay[i] += dealAmount;
              currentPeriodTotal += dealAmount;
              break;
            }
          }
        });
        
        dailyRevenue.value = revenueByDay;
        totalRevenue.value = currentPeriodTotal;
        
        // Calculate growth percentage compared to previous 7 days
        let previousPeriodTotal = 0;
        deals.value.forEach(deal => {
          const dealAmount = typeof deal.amount === 'string' ? parseFloat(deal.amount) : deal.amount;
          if (isNaN(dealAmount)) return;
          
          const dealDate = new Date(deal.created_at);
          const daysSinceToday = Math.floor((today.getTime() - dealDate.getTime()) / (1000 * 60 * 60 * 24));
          
          // Check if the deal is from the previous 7 days (8-14 days ago)
          if (daysSinceToday >= 7 && daysSinceToday < 14) {
            previousPeriodTotal += dealAmount;
          }
        });
        
        // Calculate growth percentage
        if (previousPeriodTotal > 0) {
          growthPercentage.value = Math.round(((currentPeriodTotal - previousPeriodTotal) / previousPeriodTotal) * 100);
        } else if (currentPeriodTotal > 0) {
          growthPercentage.value = 100; // If previous period had 0 revenue, growth is 100%
        } else {
          growthPercentage.value = 0; // If both periods had 0 revenue, growth is 0%
        }
        
      } catch (error) {
        console.error('Error in fetchDealsData:', error);
      } finally {
        loading.value = false;
      }
    };

    onMounted(() => {
      isClient.value = true;
      fetchDealsData();
    });

    return {
      isClient,
      loading,
      revenue,
      revenueGrowth,
      totalRevenue,
      growthPercentage,
      currencySymbol,
      directT,
    };
  },
});
</script>
